import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { Controller } from "../interface/controller.interface";
import { HTTPMethod } from "../model/http-method.enum";
import { HTTPRequestBuilder } from "../model/http.request.model";
import { HTTPService } from "./http.service";
import { orElse } from "../model/operator/or-else.operator";
import { OrderEvent } from "../persistence/dto/order-event.model";
import { OrderEventForGet } from "../persistence/view-dto/order-event-for-get.model";
import { OrderEventForCreation } from "../persistence/view-dto/order-event-for-creation.model";
import { OrderEventTracking } from "../persistence/dto/order-event-tracking.model";
import { OrderEventTrackingForCreation } from "../persistence/view-dto/order-event-tracking-for-creation.model";
import { OrderEventForProductsPlane } from "../persistence/view-dto/order-event-for-products-plane.model";
import { TypeSystem } from "../support/type-system.support";
import { PagedQuery, PagedQueryBuilder } from "../persistence/dto/paged-query.model";
import { PagedQueryResult } from "../persistence/dto/paged-query-result.model";
import { OrderEventSearch } from "../persistence/view-dto/order-event-search.model";
import { Optional } from "../support/optional.model";

	/**
	 * @description
	 *	Servicio reflejo del controlador de CyR (cambios y reclamos), y NC
	 *	(notas de crédito), del backend.
	 *
	 * @see gigot-backend/Controllers/Orders/OrderEventController.cs
	 */

@Injectable({ providedIn: "root" })
export class OrderEventService implements Controller {

	constructor(private httpService: HTTPService) {}

	public basePath(): string {
		return "api/orderEvent";
	}

	public checkControlStatusByUserID(applicationUserId: string): Observable<boolean> {
		return this.httpService.send<typeof undefined, boolean>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.GET)
                .controllerPath(this.basePath())
				.relativePath(`control/${applicationUserId}`)
				.build());
	}

	public controlledOrderEvents(): Observable<OrderEventForProductsPlane[]> {
		return this.httpService.send<typeof undefined, OrderEventForProductsPlane[]>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.GET)
                .controllerPath(this.basePath())
				.relativePath(`controlList`)
				.build())
			.pipe(
				orElse<OrderEventForProductsPlane[]>([] as OrderEventForProductsPlane[]),
				map(orderEvents => orderEvents.map(orderEvent => OrderEventForProductsPlane.from(orderEvent))));
	}

	public createOrderEvent(orderEvent: OrderEventForCreation): Observable<OrderEvent> {
		return this.httpService.send<OrderEventForCreation, OrderEvent>(
            new HTTPRequestBuilder<OrderEventForCreation>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("")
                .payload(orderEvent)
                .build())
			.pipe(
				map(orderEvent => OrderEvent.from(orderEvent)));
	}

	public createOrderEventTracking(orderEventId: number, orderEventTracking: OrderEventTrackingForCreation): Observable<OrderEventTracking> {
		return this.httpService.send<OrderEventTrackingForCreation, OrderEventTracking>(
            new HTTPRequestBuilder<OrderEventTrackingForCreation>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath(`orderEventTracking/${orderEventId}`)
                .payload(orderEventTracking)
                .build())
			.pipe(
				map(orderEventTracking => OrderEventTracking.from(orderEventTracking)));
	}

	public deleteOrderEventById(id: number): Observable<typeof TypeSystem.EmptyObject> {
        return this.httpService.send<typeof undefined, null>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.DELETE)
                .controllerPath(this.basePath())
                .relativePath(`${id}`)
                .build())
            .pipe(map(() => TypeSystem.EmptyObject));
    }

	public findOrderEventByID(id: number): Observable<OrderEventSearch> {
        return this.findOrderEvents(
			new PagedQueryBuilder<any>()
				.filter({ id })
				.pageNumber(1)
				.pageSize(1)
				.build())
			.pipe(
				map(pagedQueryResult => TypeSystem
					.orElse(OrderEventSearch.from(pagedQueryResult.items[0]), OrderEventSearch.empty())));
    }

	public findOrderEvents<T>(pagedQuery: PagedQuery<T>): Observable<PagedQueryResult<OrderEventSearch>> {
        return this.httpService.send<PagedQuery<T>, PagedQueryResult<OrderEventSearch>>(
            new HTTPRequestBuilder<PagedQuery<T>>()
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath("find")
                .payload(pagedQuery)
                .build());
    }

	public orderEventByID(id: number): Observable<OrderEventForGet> {
		return this.httpService.send<typeof undefined, OrderEventForGet>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.GET)
                .controllerPath(this.basePath())
				.relativePath(`${id}`)
				.build())
			.pipe(
				map(orderEvent => OrderEventForGet.from(orderEvent)));
	}

	public orderEventsByOrderID(orderId: number): Observable<OrderEvent[]> {
		return this.httpService.send<typeof undefined, OrderEvent[]>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.GET)
                .controllerPath(this.basePath())
				.relativePath(`order/${orderId}`)
				.build())
			.pipe(
				orElse<OrderEvent[]>([] as OrderEvent[]),
				map(orderEvents => orderEvents.map(orderEvent => OrderEvent.from(orderEvent))));
	}

	public tryCheckControlStatusByUserID(applicationUserId: string): Observable<Optional<boolean>> {
		return Optional.of(applicationUserId)
			.asyncMap(applicationUserId => this.checkControlStatusByUserID(applicationUserId));
	}
}
